import React, { useEffect, useState } from 'react';
import '../../utils/Style/pages/PayrollMobile.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripLines } from '@fortawesome/free-solid-svg-icons';
import useLoginAPI from '../../api/loginAPI';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { setDeviceType, setIsApproveAll, setMobileResponse } from '../../features/appMobileSlice';
import useAuthorizationMobileApi from '../../api/authorizationMobileApi';
import PayrollApi from '../../api/PayrollAPI';
import { setLoading } from '../../features/responseSlice';
import useResponseMobile from '../../app/useResponseMobile';
import { resetPayrollRefresh } from '../../features/webSocketSlice';
import BlurLoading from '../../components/BlurLoading';
import UtcToLocal from '../../components/UtcToLocal';

function PayrollMobile({ setUrlData }) {
  const [searchInput, setSearchInput] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedCurrency, setSelectedCurrency] = useState("Both");

  const info = useSelector((state) => state.transferResponse.info);
  const payrollSummary = info.payrollSummary?.PayrollSummary;
  const payrollInfoList = info.payrollSummary?.PayrollInfoList;

  const selectedFlatRows = payrollInfoList?.map((item) => ({
    original: {
      PayrollId: item.PayrollId,
    },
  })) || [];

  const filteredPayrollInfoList = payrollInfoList?.filter((item) => {
    const currencyMatch =
      selectedCurrency === "Both" ||
      item.Currency.toUpperCase() === selectedCurrency.toUpperCase();

    const searchTerm = searchInput.trim().toLowerCase();
    const searchMatch =
      searchTerm === "" ||
      item.RecepientName.toLowerCase().includes(searchTerm) ||
      item.PhoneNumber.toLowerCase().includes(searchTerm);
    return currencyMatch && searchMatch;
  });

  const { MobileLogin } = useLoginAPI();
  const {
    GetPayrollSummaryByReferenceNoMobile,
    GetPayrollSubmittedMobile,
    ApprovePayrollMobile,
    RejectPayrollMobile,
  } = PayrollApi();
  const { generateAuthorizationCodeMobile, MobileAuthorize } = useAuthorizationMobileApi();
  const { successResponseMobile, errorResponseMobile } = useResponseMobile();

  const payrollRefresh = useSelector((state) => state.websocket.payrollRefresh);
  const payrollsOverviewMobile = useSelector((state) => state.appData.payrollsOverviewMobile);

  const dispatch = useDispatch();
  const { id: UserAccountId, ts: TimeStamp, ref: reference, device: deviceType, sh: encodedSecureHash } = useParams();

  sessionStorage.setItem("secureHashencode", encodeURIComponent(encodedSecureHash));

  useEffect(() => {
    dispatch(setDeviceType(deviceType));
  }, [deviceType]);

  const SecureHash = decodeURIComponent(encodedSecureHash);
  if (!sessionStorage.getItem("SecureHashSaved")) {
    sessionStorage.setItem("SecureHashSaved", SecureHash);
  }
  const secureHashSaved = SecureHash;

  useEffect(() => {
    setUrlData({
      id: UserAccountId,
      ts: TimeStamp,
      reference: reference,
      secure: secureHashSaved,
      deviceType: deviceType,
    });
  }, [UserAccountId, TimeStamp, reference, deviceType, secureHashSaved]);

  useEffect(() => {
    async function fetchData() {
      const sessionMobile = sessionStorage.getItem("SessionMobile");
      const tokenMobile = sessionStorage.getItem("TokenMobile");

      const isInvalid =
        !sessionMobile ||
        !tokenMobile ||
        sessionMobile === "null" ||
        tokenMobile === "null" ||
        sessionMobile.trim() === "" ||
        tokenMobile.trim() === "";

      if (isInvalid) {
        try {
          await MobileLogin(UserAccountId, TimeStamp, reference, 1, secureHashSaved);
        } catch (error) {
          console.error(error);
        }
      }
      await GetPayrollSummaryByReferenceNoMobile(reference);
      await GetPayrollSubmittedMobile("", selectedCurrency, selectedStatus);
    }
    fetchData();
  }, [searchInput, selectedCurrency, selectedStatus, UserAccountId, TimeStamp, reference, secureHashSaved]);

  useEffect(() => {
    if (payrollRefresh) {
      GetPayrollSummaryByReferenceNoMobile(reference);
      GetPayrollSubmittedMobile("", selectedCurrency, selectedStatus);
      dispatch(resetPayrollRefresh());
    }
  }, [payrollRefresh, searchInput, selectedCurrency, selectedStatus]);

  const handleAcceptAll = async (method, singlePayrollId = null) => {
    dispatch(setLoading("pending"));

    try {
      const response = await generateAuthorizationCodeMobile();
      const authCode = response?.Payload?.AuthorizationCode;
      if (!authCode) {
        console.error("No AuthorizationCode received");
        return;
      }

      await MobileAuthorize(authCode);

      const mobileResponsePromise = new Promise((resolve, reject) => {
        window.handleCheckout = (message) => {
          dispatch(setMobileResponse(message));
          resolve(message);
        };
        setTimeout(() => {
          reject(new Error("Mobile response timeout"));
        }, 10000);
      });

      if (deviceType === "Android") {
        setTimeout(() => {
          window.AndroidInterface.callbackHandler("message");
        }, 500);
      } else if (deviceType === "Iphone") {
        setTimeout(() => {
          window.webkit.messageHandlers.callbackHandler.postMessage("fingerprint");
        }, 500);
      }

      const mobileResponse = await mobileResponsePromise;

      const payrollIds = singlePayrollId
        ? [singlePayrollId]
        : selectedFlatRows.map((row) => row.original.PayrollId);

      const payload = {
        SessionID: sessionStorage.getItem("SessionMobile"),
        AuthorizationCode: authCode,
        Reference: info?.referenceNo || "",
        PayrollIds: payrollIds,
      };

      dispatch(setLoading("pending"));

      if (mobileResponse === "success") {
        if (method === "delete") {
          dispatch(setLoading("pending"));
          await RejectPayrollMobile(payload)
            .then((res) => {
              successResponseMobile(res);
              GetPayrollSummaryByReferenceNoMobile(reference);
              GetPayrollSubmittedMobile(searchInput, selectedCurrency, selectedStatus);
            })
            .catch((error) => {
              errorResponseMobile(error);
              dispatch(setLoading("idle"));
            });
        } else {
          dispatch(setLoading("pending"));
          await ApprovePayrollMobile(payload)
            .then((res) => {
              successResponseMobile(res);
              GetPayrollSummaryByReferenceNoMobile(reference);
              GetPayrollSubmittedMobile(searchInput, selectedCurrency, selectedStatus);
            })
            .catch((error) => {
              errorResponseMobile(error);
              dispatch(setLoading("idle"));
            });
        }
      } else {
        return false;
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setLoading("idle"));
    }
  };


  const formatStatus = (status) => {
    if (!status) return "";
    return status.startsWith("Pending") ? "Pending" : status;
  };

  const getStatusClass = (status) => {
    if (!status) return "";
    const lowerStatus = status.toLowerCase();
    if (lowerStatus.includes("rejected")) return "rejected";
    if (lowerStatus.includes("sent")) return "sent";
    return "pending";
  };

  const options = {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  };

  return (
    <div className='PayrollMobilePage'>
      <div className='search-filter'>
        <FontAwesomeIcon className="grip-icon" icon={faGripLines} />
        <input
          type='text'
          className='search'
          placeholder='Search'
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </div>

      <div className='payroll-details'>
        <div className='payroll-top'>
          <div className='top-section'>
            <div className='title'>Payroll Details</div>
            <div className='filtering'>
              <div className='filter'>
                <div className='label'>Show:</div>
                <div className='select'>
                  <select
                    className='ddselect'
                    name='currency'
                    id='currency'
                    onChange={(e) => setSelectedCurrency(e.target.value)}
                  >
                    <option value={"Both"}>Both Currencies</option>
                    <option value={"usd"}>USD</option>
                    <option value={"lbp"}>LBP</option>
                  </select>
                </div>
              </div>

            </div>
          </div>

          <div className='summary-payroll mt-2'>
            <div className='sub-title'>Summary of the payroll</div>
            <div className='summary-card'>
              <div className='card-cont'>
                <div className='top-section-card'>
                  <div className='payroll-id'>
                    <div className='payroll-id-value'>{info.referenceNo}</div>
                    <div className='payroll-id-label'>Payroll ID</div>
                  </div>
                  <div className='status'>
                    <div className={`payroll-status ${getStatusClass(payrollsOverviewMobile?.[0]?.Status)}`}>
                      {formatStatus(payrollsOverviewMobile?.[0]?.Status)}
                    </div>
                  </div>
                </div>

                <div className='br'></div>

                <div className='data'>
                  <div className='keys'>
                    <div className='key'>Date</div>
                    <div className='key'>Sender</div>
                  </div>
                  <div className='values'>
                    <div className='value'>
                      <UtcToLocal utcTimestamp={payrollsOverviewMobile?.[0]?.Date} options={options} />
                    </div>
                    <div className='value'>{payrollsOverviewMobile?.[0]?.Sender}</div>
                  </div>
                </div>

                <div className='br'></div>

                <div className='data align-items-start'>
                  <div className='keys'>
                    <div className='key'>Total Payroll Amount</div>
                  </div>
                  <div className='values'>
                    <div className='value bold'>$ {payrollSummary?.TotalPayrollAmountUSD}</div>
                    <div className='value bold'>L.L {payrollSummary?.TotalPayrollAmountLBP}</div>
                  </div>
                </div>

                <div className='br'></div>

                <div className='data'>
                  <div className='keys'>
                    <div className='key'>Total Transactions</div>
                    <div className='key'>Total Employees</div>
                  </div>
                  <div className='values'>
                    <div className='value'>{payrollSummary?.TotalTransactions}</div>
                    <div className='value'>{payrollSummary?.TotalEmployees}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className='approve'>

              <button 
                className='approve-btn' 
                onClick={() => {
                  dispatch(setIsApproveAll(true));
                  handleAcceptAll("approve")
                  }}>
                Approve {filteredPayrollInfoList?.length || 0}
              </button>
            </div>
          </div>
        </div>

        <div className='employees-list-list'>
          <div className='sub-title'>Employees listing</div>

          {filteredPayrollInfoList?.map((item, index) => (
            <div className="swipe-wrapper" key={index}>
              <div className="swipe-inner">
                <div className="employee-payroll-card">
                  <div className="card-top">
                    <div className="employee-data">
                      <div className="employee-name">{item.RecepientName}</div>
                      <div className="employee-number">+{item.PhoneNumber}</div>
                    </div>
                    <div className="employee-amount">
                      <div className="balance">
                        {item.Currency === 'USD' ? '$' : 'L.L'} {item.Amount}
                      </div>
                    </div>
                  </div>

                  <div className="br"></div>

                  <div className="employee-reason">
                    <div className="reason">Reason</div>
                    <div className="reason-data">{item.Reason}</div>
                  </div>
                </div>

                <div className="swipe-actions">
                  <div
                    className="approve-swipe"
                    onClick={() => {
                      dispatch(setIsApproveAll(false));
                      handleAcceptAll("approve", item.PayrollId)
                    }}
                  >
                    ✓
                  </div>
                  <div
                    className="cancel-swipe"
                    onClick={() => {
                      dispatch(setIsApproveAll(false));
                      handleAcceptAll("delete", item.PayrollId)
                    }}
                  >
                    ✕
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className='buttons'>
            <div className='approve'>
              <button
                className='approve-btn'
                onClick={() => {
                  dispatch(setIsApproveAll(true));
                  handleAcceptAll("approve")
                  }}
              >
                Approve {filteredPayrollInfoList?.length || 0}
              </button>
            </div>
            <div className='cancel'>
              <button
                className='cancel-btn'
                onClick={() => {
                  dispatch(setIsApproveAll(true));
                  handleAcceptAll("delete")
                  }}
              >
                Cancel Payroll
              </button>
            </div>
          </div>
        </div>
      </div>
      <BlurLoading />
    </div>
  );
}

export default PayrollMobile;
